import { lastOrDefault } from './array.helper';
import { stringEquals } from './string.helper';

/**
 * Gets the file extension from a given path.
 * @function getExtension
 * @param {string} path - The file path.
 * @param {string} [delimiter='.'] - The delimiter to use for splitting the path.
 * @returns {string | undefined} The file extension or undefined if not found.
 */
const getExtension = (path: string, delimiter = '.'): string | undefined => {
  if (!path) return undefined;

  const extension = lastOrDefault(path.split(delimiter));

  if (extension === 'quicktime') return 'mov';

  return extension;
};

/**
 * Checks if a given path has a specific file extension.
 * @function hasExtension
 * @param {string} path - The file path.
 * @param {string} extension - The file extension to check.
 * @returns {boolean} True if the path has the specified extension, false otherwise.
 */
const hasExtension = (path: string, extension: string): boolean => {
  if (!path || !extension) return false;

  return stringEquals(getExtension(path, '.')!, extension);
};

/**
 * Checks if a given path is a video file.
 * @function isVideo
 * @param {string} path - The file path.
 * @returns {boolean} True if the path is a video file, false otherwise.
 */
const isVideo = (path: string): boolean => {
  if (!path) return false;

  const extensions = ['mp4', 'webm', 'mov', 'quicktime'];

  const extension = getExtension(path, '.');

  return path.startsWith('video') || extensions.includes(extension!);
};

/**
 * Enum for asset path types.
 * @enum {number}
 */
enum AssetPathTypeEnum {
  None,
  Original,
  Thumbnail,
  Platform,
  Marketplace,
  Badge,
  Avatar,
}

/**
 * Enum for asset path options.
 * @enum {number}
 */
enum AssetPathOptionEnum {
  None,
  Primary,
  Secondary,
  CoalesceImageOnly,
}

export {
  AssetPathTypeEnum,
  AssetPathOptionEnum,
  hasExtension,
  isVideo,
  getExtension,
};
